import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useState } from 'react';
import ColumnaKanban from './ColumnaKanban';
import AsignarTrabajadorModal from './AsignarTrabajadorModal';
import { COLUMNAS } from '@/utils/tareaUtils';
import { toast } from 'sonner';

export default function VistaKanban({ 
  tareas, 
  onEdit, 
  onDelete, 
  onViewDetail,
  onUpdateEstado,
  onAsignarResponsable,
  onAsignarRevisor,
  tareaExpandida,
  onToggleExpand,
  userName,
  trabajadores
}) {
  const [activeId, setActiveId] = useState(null);
  const [modalAsignarOpen, setModalAsignarOpen] = useState(false);
  const [modalRevisorOpen, setModalRevisorOpen] = useState(false);
  const [tareaEnAsignacion, setTareaEnAsignacion] = useState(null);
  const [estadoDestino, setEstadoDestino] = useState(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const getTareasPorEstado = (estado) => {
    return tareas.filter(t => t.estado === estado);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const tareaId = active.id;
    let columnaDestino = over.id;

    // Si se soltó sobre otra tarea, encontrar la columna de esa tarea
    const tareaDestino = tareas.find(t => t.id === columnaDestino);
    if (tareaDestino) {
      columnaDestino = tareaDestino.estado;
    }

    // Verificar que la columna destino es válida
    if (!COLUMNAS.find(col => col.id === columnaDestino)) return;

    const tarea = tareas.find(t => t.id === tareaId);
    if (!tarea) return;

    // VALIDACIÓN DE FLUJO SECUENCIAL
    const estadoOrden = {
      'por_hacer': 0,
      'asignado': 1,
      'en_proceso': 2,
      'en_revision': 3,
      'finalizado': 4
    };

    const estadoActualOrden = estadoOrden[tarea.estado];
    const estadoDestinoOrden = estadoOrden[columnaDestino];

    // VALIDACIÓN 0: No se puede ir a FINALIZADO sin pasar por EN REVISIÓN
    if (columnaDestino === 'finalizado' && tarea.estado !== 'en_revision') {
      toast.error(
        '❌ No se puede finalizar esta tarea directamente. Debe pasar por: Asignado → En Proceso → En Revisión → Finalizado',
        { duration: 5000 }
      );
      return;
    }

    // VALIDACIÓN 1: Si se mueve a "ASIGNADO" y no tiene responsable
    if (columnaDestino === 'asignado' && !tarea.responsable_id) {
      setTareaEnAsignacion(tarea);
      setEstadoDestino(columnaDestino);
      setModalAsignarOpen(true);
      return;
    }

    // VALIDACIÓN 2: Si se intenta saltar más de una columna (excepto retroceder)
    if (estadoDestinoOrden > estadoActualOrden + 1) {
      const estadosRequeridos = [];
      if (estadoActualOrden < 1 && estadoDestinoOrden > 1) estadosRequeridos.push('Asignado');
      if (estadoActualOrden < 2 && estadoDestinoOrden > 2) estadosRequeridos.push('En Proceso');
      if (estadoActualOrden < 3 && estadoDestinoOrden > 3) estadosRequeridos.push('En Revisión');
      
      toast.error(
        `❌ No puede saltar etapas. Debe pasar primero por: ${estadosRequeridos.join(' → ')}`,
        { duration: 4000 }
      );
      return;
    }

    // VALIDACIÓN 3: Si se mueve a "EN REVISIÓN" y no tiene revisor
    if (columnaDestino === 'en_revision' && !tarea.revisor_id) {
      setTareaEnAsignacion(tarea);
      setEstadoDestino(columnaDestino);
      setModalRevisorOpen(true);
      return;
    }

    // VALIDACIÓN 4: No se puede ir a EN PROCESO sin tener responsable
    if (columnaDestino === 'en_proceso' && !tarea.responsable_id) {
      toast.error(
        '❌ Esta tarea debe tener un responsable asignado antes de pasar a En Proceso',
        { duration: 4000 }
      );
      return;
    }

    // Si pasa todas las validaciones, proceder normalmente
    await onUpdateEstado(tareaId, columnaDestino, userName);
  };

  const handleAsignarResponsable = async (trabajador) => {
    if (!tareaEnAsignacion || !estadoDestino) return;

    // Asignar responsable a la tarea
    const success = await onAsignarResponsable(
      tareaEnAsignacion.id, 
      trabajador.id, 
      trabajador.nombre_completo
    );

    if (success) {
      // Actualizar estado después de asignar
      await onUpdateEstado(tareaEnAsignacion.id, estadoDestino, userName);
      toast.success(`${trabajador.nombre_completo} asignado como responsable`);
    }

    // Limpiar
    setTareaEnAsignacion(null);
    setEstadoDestino(null);
  };

  const handleAsignarRevisor = async (trabajador) => {
    if (!tareaEnAsignacion || !estadoDestino) return;

    // Asignar revisor a la tarea
    const success = await onAsignarRevisor(
      tareaEnAsignacion.id,
      trabajador.id,
      trabajador.nombre_completo
    );

    if (success) {
      // Actualizar estado después de asignar
      await onUpdateEstado(tareaEnAsignacion.id, estadoDestino, userName);
      toast.success(`${trabajador.nombre_completo} asignado como revisor`);
    }

    // Limpiar
    setTareaEnAsignacion(null);
    setEstadoDestino(null);
  };

  const tareaActiva = activeId ? tareas.find(t => t.id === activeId) : null;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {COLUMNAS.map((columna) => {
            const tareasColumna = getTareasPorEstado(columna.id);
            return (
              <ColumnaKanban
                key={columna.id}
                columna={columna}
                tareas={tareasColumna}
                onEdit={onEdit}
                onDelete={onDelete}
                onViewDetail={onViewDetail}
                tareaExpandida={tareaExpandida}
                onToggleExpand={onToggleExpand}
              />
            );
          })}
        </div>

        <DragOverlay>
          {tareaActiva ? (
            <div className="bg-white rounded-lg shadow-lg border-2 border-emerald-500 p-3">
              <h4 className="font-semibold text-sm">{tareaActiva.titulo}</h4>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Modal para asignar responsable */}
      <AsignarTrabajadorModal
        open={modalAsignarOpen}
        onOpenChange={setModalAsignarOpen}
        trabajadores={trabajadores}
        titulo="Asignar Responsable"
        descripcion={`Para mover "${tareaEnAsignacion?.titulo}" a ASIGNADO, debe seleccionar un responsable.`}
        onAsignar={handleAsignarResponsable}
        tipo="responsable"
      />

      {/* Modal para asignar revisor */}
      <AsignarTrabajadorModal
        open={modalRevisorOpen}
        onOpenChange={setModalRevisorOpen}
        trabajadores={trabajadores}
        titulo="Asignar Revisor"
        descripcion={`Para mover "${tareaEnAsignacion?.titulo}" a EN REVISIÓN, debe seleccionar un revisor.`}
        onAsignar={handleAsignarRevisor}
        tipo="revisor"
      />
    </>
  );
}
